/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, October 18th 2021, 7:19:38 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import { Box, BoxProps } from "@mui/material";
import { RightAlignContainer } from "ui-widgets";
import * as React from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import { OSLoadingButton } from "ui-widgets";
import { FormState, UseFormReturn, FormProvider } from "react-hook-form";
import { OSFormProvider, OSFormProviderProps } from "ui-widgets/forms/providers";
import { OSFormInputProps } from "./types";
import "theme/css/OSForm.css";

/**
 * Reusable form component for any kind of form, even a form that does not post to a onestop API endpoint.
 */

const getChildrenToDisplay = <TFormData,>(children: React.ReactNode, fieldsToShow?: (keyof TFormData)[]) => {
  return React.Children.map<any, React.ReactNode>(children, (child) => {
    const child_schema_field_key = (child as React.ReactElement<OSFormInputProps<TFormData>, any>)?.props
      ?.schema_field_key;

    if (!child_schema_field_key) return child;

    if (fieldsToShow.includes(child_schema_field_key)) return child;
  });
};

export interface OSFormProps<T> extends Partial<FormState<any>>, BoxProps, OSFormProviderProps<T> {
  onFormSubmit: (e?: React.BaseSyntheticEvent) => void;
  onFormCancel?: () => void;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  children: React.ReactNode;
  fieldsToShow?: (keyof T)[];
  useFormReturn: UseFormReturn<T>;
  submitButtonProps?: ButtonProps;
}

export const OSForm = <T,>(props: OSFormProps<T>) => {
  const { onFormSubmit, showSubmitButton = true, fieldsToShow = [] } = props;

  const submitFunction = (event: React.FormEvent) => {
    event.preventDefault();
    onFormSubmit();
  };

  const childrenToDisplay =
    fieldsToShow.length > 0 ? getChildrenToDisplay<T>(props.children, fieldsToShow) : props.children;

  return (
    <FormProvider {...props.useFormReturn}>
      <OSFormProvider validationSchema={props.validationSchema}>
        <Box
          component="form"
          sx={
            props.sx ?? {
              "& .MuiFormControl-root": { mb: 2, width: "100%" },
            }
          }
          noValidate
          autoComplete="off"
          onSubmit={submitFunction}
          className={props.className ?? "os-inline-form"}
          {...props}
        >
          {childrenToDisplay}

          {(showSubmitButton || props.onFormCancel) && (
            <RightAlignContainer>
              <DialogActions>
                {props.onFormCancel ? (
                  <React.Fragment>
                    <Button size="large" className="os-min-sized-button" variant="text" onClick={props.onFormCancel}>
                      Cancel
                    </Button>
                  </React.Fragment>
                ) : null}

                {showSubmitButton ? (
                  <OSLoadingButton
                    size="large"
                    className="os-min-sized-button"
                    variant="contained"
                    isLoading={props.isSubmitting}
                    type="submit"
                    {...props.submitButtonProps}
                  >
                    {props.submitButtonText ?? "Save"}
                  </OSLoadingButton>
                ) : null}
              </DialogActions>
            </RightAlignContainer>
          )}
        </Box>
      </OSFormProvider>
    </FormProvider>
  );
};
